(() => {
  if (customElements.get('rdb-subscriptions')) return;

  class RdbSubscriptions extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.forms = new Map();
      cancelAnimationFrame(this.priceFrame);
      const refreshPrices = () => {
        cancelAnimationFrame(this.priceFrame);
        this.priceFrame = requestAnimationFrame(() => this.syncPriceOptions());
      };
      document.addEventListener('rdb:currencychange', refreshPrices, { signal: this.controller.signal });
      document.addEventListener('futureblink:currencychange', refreshPrices, { signal: this.controller.signal });
      refreshPrices();
      const tomorrow = new Date();
      tomorrow.setHours(12, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const minimum = [tomorrow.getFullYear(), String(tomorrow.getMonth() + 1).padStart(2, '0'), String(tomorrow.getDate()).padStart(2, '0')].join('-');

      this.querySelectorAll('.ros-sub-plan__form').forEach((form) => {
        const state = { plans: {}, pending: false, button: form.querySelector('[type="submit"]') };
        state.original = state.button.innerHTML;
        try { state.plans = JSON.parse(form.querySelector('[data-variant-plans]').textContent); } catch { state.plans = {}; }
        this.forms.set(form, state);
        form.querySelectorAll('input[type="date"]').forEach((input) => { input.min = minimum; });
        form.addEventListener('change', () => this.syncPlans(form), { signal: this.controller.signal });
        form.addEventListener('submit', (event) => this.submit(event, form), { signal: this.controller.signal });
        this.syncPlans(form);
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.priceFrame);
      this.forms?.forEach((state) => {
        state.button.innerHTML = state.original;
        state.button.disabled = false;
        state.button.removeAttribute('aria-busy');
      });
      this.forms?.clear();
    }

    syncPriceOptions() {
      if (!this.isConnected || !window.rdbCurrencyPrices?.format) return;
      this.querySelectorAll('option[data-subscription-price]').forEach((option) => {
        const price = window.rdbCurrencyPrices.format(option.dataset.subscriptionPrice, option.dataset.subscriptionBase);
        if (!price) return;
        const text = `${option.dataset.subscriptionPriceTitle} — \u2066${price}\u2069${option.dataset.subscriptionPriceSuffix}`;
        if (option.textContent !== text) option.textContent = text;
      });
    }

    syncPlans(form) {
      const state = this.forms.get(form);
      const id = form.elements.namedItem('id').value;
      const allowed = (state.plans[id] || []).map(String);
      const select = form.elements.namedItem('selling_plan');
      for (const option of select.options) option.disabled = Boolean(option.value) && !allowed.includes(option.value);
      if (select.value && !allowed.includes(select.value)) select.value = '';
      select.disabled = allowed.length === 0;
      state.button.disabled = state.pending || allowed.length === 0;
    }

    async submit(event, form) {
      event.preventDefault();
      const state = this.forms.get(form);
      if (state.pending || !form.reportValidity()) return;
      const values = new FormData(form);
      const id = String(values.get('id') || '');
      const sellingPlan = String(values.get('selling_plan') || '');
      const allowed = (state.plans[id] || []).map(String);
      const error = form.querySelector('[data-subscription-error]');
      if (!id || !sellingPlan || !allowed.includes(sellingPlan)) {
        this.showError(error, this.dataset.choiceError);
        return;
      }
      const properties = {};
      for (const [name, value] of values) {
        if (name.startsWith('properties[') && name.endsWith(']')) properties[name.slice(11, -1)] = String(value).trim();
      }
      const controller = this.controller;
      state.pending = true;
      state.button.disabled = true;
      state.button.setAttribute('aria-busy', 'true');
      state.button.textContent = this.dataset.preparingLabel;
      error.hidden = true;
      const shopRoot = window.Shopify?.routes?.root || '/';
      const prefix = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
      let customerError = this.dataset.addError;
      try {
        const response = await fetch(`${prefix}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1, selling_plan: sellingPlan, properties }] }),
          signal: controller.signal
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (typeof payload.description === 'string') customerError = payload.description;
          else if (typeof payload.message === 'string') customerError = payload.message;
          throw new Error('subscription_request_rejected');
        }
        if (!controller.signal.aborted && this.isConnected) window.location.assign(`${prefix}cart`);
      } catch {
        if (controller.signal.aborted) return;
        this.showError(error, customerError);
        state.pending = false;
        state.button.innerHTML = state.original;
        state.button.removeAttribute('aria-busy');
        this.syncPlans(form);
      }
    }

    showError(node, message) {
      node.textContent = message;
      node.hidden = false;
    }
  }

  customElements.define('rdb-subscriptions', RdbSubscriptions);
})();
